import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorPanel, Pill, Refused } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { LoadFailure } from '../LoadFailure.js';
import { usePermissions } from '../permissions.js';
import { insights, type FindingSummary } from './insightsClient.js';
import {
  entitySentence,
  pillVariantFor,
  recurrenceSentence,
  severityLabel,
  subjectsOf,
} from './insightsLogic.js';
import {
  OPEN_STATES,
  PANEL_LIMIT,
  allInsightsHref,
  allInsightsLabel,
  emptyLine,
  frameLine,
  moreLine,
  scopeId,
  type FindingScope,
} from './contextualLogic.js';

/**
 * INTELLIGENCE, WHERE THE WORK ALREADY IS.
 *
 * ===========================================================================
 * WHY THIS EXISTS
 * ===========================================================================
 * Today a person goes TO Insights to ask a question. This is the other
 * direction: the same findings, the same reach rules, the same server, shown
 * on the page where they are already standing — one worker's record, one
 * program. It is a DOORWAY to Insights, not a second Insights: no filters, no
 * views, no history, no evidence. Five open rows and a link.
 *
 * ===========================================================================
 * THIS PANEL IS NEVER MOUNTED ON A WORKER'S OWN VIEW
 * ===========================================================================
 * `OPERATIONAL-LIBRARY-V1.md` §2.1 — "Management sees intelligence; workers
 * see priorities." A worker meets a finding as the TASK it produced; the
 * finding itself, with its detector id, its confidence and its recurrence
 * count, is a management artifact.
 *
 * So `worker-web/WorkerWeb.tsx` and `worker-preview/WorkerPreview.tsx` do not
 * import this file, and `worker-findings-panel-exclusion.test.tsx` mounts
 * WorkerWeb and proves the panel is not in it. The scope type in
 * `contextualLogic.ts` has no variant a worker's own view could construct —
 * `{ kind: 'worker', workerId }` means "the worker this MANAGER is looking
 * at", and the worker's own screens have no worker id to pass, deliberately
 * (`WorkerWeb`'s docblock: "there is no parameter on this screen that could
 * name somebody else").
 *
 * The server agrees rather than merely permitting it: migration
 * `1813001000000` grants `insights:read` to org_admin and supervisor and NOT
 * to worker, citing the same paragraph. Two independent refusals, and this
 * one is the one that decides what is on the page.
 *
 * ===========================================================================
 * AND IT IS NOT AN EXISTENCE ORACLE
 * ===========================================================================
 * A contextual panel is a tempting place to leak, because it is keyed to one
 * named subject and its layout is read at a glance. Four things stop it:
 *
 *   1. THE CARD IS A FIXED SLOT. The `<section>` and its `<h2>` render in
 *      every phase — loading, refused, failed, empty, and with rows. Nothing
 *      about whether a finding exists is carried by whether the card is
 *      there, by its height settling, or by a skeleton that appears and then
 *      takes the whole block with it when it goes.
 *   2. THE FILTER NARROWS, IT NEVER WIDENS. `workerId`/`programId` are applied
 *      inside the same WHERE clause as the reach predicate
 *      (`repository.ts:listFindings`), so a finding the caller cannot reach is
 *      not in `items` and is not in `total`. Asking about a subject you may
 *      not reach and asking about one with nothing open return the same empty
 *      page — the integration suite asserts the two responses are equal byte
 *      for byte.
 *   3. NO NUMBER IS RECONSTRUCTED. The only count on screen is `moreLine`,
 *      computed from the server's `total`, which is the caller's total.
 *   4. WITHHELD IS STILL WITHHELD INSIDE A ROW. Entity names go through
 *      `entitySentence`, which keeps shown / withheld / absent as three
 *      different sentences instead of one blank.
 *
 * ===========================================================================
 * THE TRIAGE AFFORDANCE, AND WHAT IT DELIBERATELY OMITS
 * ===========================================================================
 * Acknowledge is here. Dismiss is NOT, and its absence is a decision.
 *
 * A dismissal is a durable named act: it writes an append-only `finding_event`
 * carrying a required reason and the dismisser's identity, a second dismissal
 * cannot overwrite the first, and nothing in the data model can take one back.
 * The honest place to make a decision like that is the finding's own page,
 * where the evidence, the recommended actions and the whole history are on
 * screen — not a five-row card beside somebody's schedule, where the reader
 * has not seen what they are dismissing. So the panel says what a dismissal
 * costs and sends the reader to the finding to make it.
 *
 * Acknowledge is offered because it claims nothing: it moves a finding from
 * "nobody has looked" to "somebody is watching", which is a fact the reader is
 * in a position to assert from here. It is still permanent, and there is no
 * undo on it, because there is no undo behind it — the row STAYS in the panel
 * with its pill changed to Watching, rather than vanishing in a way that would
 * imply it had been cleared.
 */

/** The reading key, and the one the server's guard is argued from. */
const INSIGHTS_READ_KEY = 'insights:read';
/** Acknowledging is a separate authority from reading. */
const INSIGHTS_TRIAGE_KEY = 'insights:triage';

export interface FindingsPanelProps {
  readonly scope: FindingScope;
}

export function FindingsPanel({ scope }: FindingsPanelProps) {
  const navigate = useNav();
  const qc = useQueryClient();
  const {
    has,
    isLoading: permissionsLoading,
    isError: permissionsError,
    refetch: refetchPermissions,
  } = usePermissions();
  const id = scopeId(scope);

  /**
   * UNKNOWN IS NOT DENIED — `RequirePermission`'s rule, and the reason this is
   * a tri-state rather than `has(...)`. While `/auth/me` is in flight the
   * answer is not "no"; rendering `Refused` there would tell a reader who
   * holds the key that they do not.
   *
   * A FAILED permission read is its own state and is NOT folded in here. It was
   * once, and the result was a skeleton that never resolved: `isError` does not
   * clear on its own, so "not yet known" became "never known" and the panel spun
   * for as long as the page was open. Nothing is loading and nobody is coming
   * back to it, which is the quietest kind of lie a screen can tell.
   */
  const mayRead: boolean | undefined = permissionsLoading ? undefined : has(INSIGHTS_READ_KEY);

  const list = useQuery({
    queryKey: ['insights', 'findings', 'contextual', scope.kind, id],
    queryFn: () =>
      insights.list({
        state: [...OPEN_STATES],
        ...(scope.kind === 'worker'
          ? { workerId: scope.workerId }
          : { programId: scope.programId }),
        limit: PANEL_LIMIT,
      }),
    // Not fired when we already know the answer is no. The 403 branch below
    // stays anyway: this set is cached for five minutes, and a permission
    // revoked inside that window is a refusal only the server can report.
    enabled: mayRead === true && !permissionsError && id !== '',
  });

  const acknowledge = useMutation({
    mutationFn: (findingId: string) => insights.acknowledge(findingId),
    onSuccess: () => {
      // Every findings read, contextual and otherwise: acknowledging moves the
      // row's state and the Insights queue must not keep showing it as unseen.
      void qc.invalidateQueries({ queryKey: ['insights'] });
    },
  });

  if (id === '') return null;

  const items = list.data?.items ?? [];
  // READ OFF THE RESPONSE. The server said which population it answered about;
  // re-deriving it from the reader's permissions is how a narrowed answer comes
  // to be labelled organization-wide.
  const organizationWide = list.data?.organizationWide;
  const more = moreLine(list.data?.total, items.length);
  const canTriage = mayRead === true && has(INSIGHTS_TRIAGE_KEY);

  return (
    <section className="rn-card" aria-labelledby={`findings-panel-${scope.kind}`}>
      <div className="rn-card-head">
        <h2 className="rn-card-title" id={`findings-panel-${scope.kind}`}>
          What Phibian noticed
        </h2>
        {mayRead === true && !permissionsError ? (
          <button
            type="button"
            className="rn-link"
            onClick={() => navigate(allInsightsHref(scope))}
          >
            {allInsightsLabel(scope)}
          </button>
        ) : null}
      </div>

      {permissionsError ? (
        // WE DO NOT KNOW WHETHER THIS READER MAY LOOK, and saying either answer
        // would be a guess. `ErrorPanel` with a retry, because retrying is
        // exactly what can fix it — the same shape `RequirePermission` uses for
        // the same failure.
        <ErrorPanel
          message="We could not check whether you can see what Phibian noticed. This is not a refusal — we do not know yet."
          onRetry={() => refetchPermissions()}
        />
      ) : mayRead === false ? (
        // The reader's own permission set already answered. No read is fired
        // and no retry is offered — asking again cannot change it.
        <Refused
          heading="What Phibian noticed is not shown to you"
          message="This panel is withheld, not empty. Reading findings needs the Insights permission; nothing has been left out by mistake."
        />
      ) : (
        <>
          <p className="rn-muted">{frameLine(scope, organizationWide)}</p>
          <ListState
            phase={mayRead === undefined ? { kind: 'loading' } : listPhase(list, items.length)}
            surface="table"
            rows={2}
            failedMessage="We could not load what Phibian noticed. Nothing here has changed."
            onRetry={() => void list.refetch()}
            refused={
              <Refused
                heading="What Phibian noticed is not shown to you"
                message="This panel is withheld, not empty. Nothing has been left out by mistake."
              />
            }
            empty={<p className="rn-muted">{emptyLine(scope, organizationWide)}</p>}
          >
            <ul className="rn-list">
              {items.map((row) => (
                <FindingRow
                  key={row.id}
                  row={row}
                  canTriage={canTriage}
                  pending={acknowledge.isPending && acknowledge.variables === row.id}
                  onAcknowledge={() => acknowledge.mutate(row.id)}
                  onOpen={() => navigate(`/insights/${row.id}`)}
                />
              ))}
            </ul>
          </ListState>
          {more ? <p className="rn-muted">{more}</p> : null}
          {canTriage && items.length > 0 ? (
            // Said ONCE, beside the control that is not offered, because the
            // reader's next question after "Acknowledge" is "how do I make it
            // go away" and the honest answer is that they cannot from here —
            // and that when they can, it will not come back.
            <p className="rn-muted" style={{ fontSize: 13 }}>
              Dismissing a finding needs a reason and is permanent — it is recorded against your
              name and cannot be undone. Open the finding to do it.
            </p>
          ) : null}
          {acknowledge.isError ? (
            // No `onRetry`: a refusal is an answer, and for anything else the
            // reader can press the row's own button again.
            <LoadFailure
              error={acknowledge.error}
              message="We could not record that. Nothing has changed."
              refusedHeading="You cannot triage this finding"
            />
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * One row.
 *
 * `pending` is keyed to THIS row's id rather than to the mutation being in
 * flight at all — with one shared mutation, `isPending` alone puts every row
 * into a working state when one of them was pressed.
 */
function FindingRow({
  row,
  canTriage,
  pending,
  onAcknowledge,
  onOpen,
}: {
  row: FindingSummary;
  canTriage: boolean;
  pending: boolean;
  onAcknowledge: () => void;
  onOpen: () => void;
}) {
  const subjects = subjectsOf(row.entities).map(entitySentence).join(' · ');
  return (
    <li className="rn-col" style={{ gap: 'var(--space-8)' }}>
      <div className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
        <span className="rn-strong">{row.title}</span>
        <Pill variant={pillVariantFor(row.severity)}>{severityLabel(row.severity)}</Pill>
        {/* The state is a WORD, not the row's absence. An acknowledged finding
            stays here reading "Watching" — it has not been cleared, and a panel
            that removed it would say it had. */}
        {row.state === 'watching' ? <span className="rn-muted">Watching</span> : null}
      </div>
      <span className="rn-muted">
        {subjects || 'No subject recorded'} · {recurrenceSentence(row.recurrenceCount)}
      </span>
      <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
        <button type="button" className="rn-link" onClick={onOpen}>
          Open finding →
        </button>
        {canTriage && row.state === 'needs_attention' ? (
          <button type="button" className="rn-btn-quiet" onClick={onAcknowledge} disabled={pending}>
            {pending ? 'Recording…' : 'Acknowledge'}
          </button>
        ) : null}
      </div>
    </li>
  );
}
